import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Form, Formik, type FormikErrors, type FormikTouched } from "formik";
import { useMemo, type ReactNode } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Field, FieldError } from "@volca/ui/components/ui/field";
import { Input } from "@volca/ui/components/ui/input";
import {
  SimpleSidebar,
  SimpleSidebarHeader,
} from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { RosterFieldControl } from "@/payroll/roster/roster-field-control";
import {
  type PayrollRosterContract,
  type PayrollRosterField,
  type PayrollRosterMember,
  type PayrollRosterFormValues,
  type PayrollTeam,
} from "@/payroll/roster/types";
import {
  type PayrollRosterMemberErrors,
  validatePayrollRosterMember,
  toPayrollRosterFormValues,
  toPayrollRosterMember,
} from "@/payroll/roster/utils";
import { DrawerField } from "./drawer-field";
import { RosterEditDrawerFooter } from "./roster-edit-drawer-footer";
import { RosterMemberAccessFields } from "./roster-member-access-fields";

const toFormikErrors = (
  errors: PayrollRosterMemberErrors,
): FormikErrors<PayrollRosterMember> => ({
  ...(errors.display_name ? { display_name: errors.display_name } : {}),
  ...(errors.service_titan_employee_id
    ? { service_titan_employee_id: errors.service_titan_employee_id }
    : {}),
  ...(Object.keys(errors.field_values).length > 0
    ? { field_values: errors.field_values }
    : {}),
});

interface RosterEditDrawerProps {
  businessId: string;
  teams: PayrollTeam[];
  accessFields?: ReactNode;
  title: string;
  fields: PayrollRosterField[];
  // fixed_option choices per field key, sourced live from the contract.
  fieldOptions: Record<string, string[]>;
  contract: PayrollRosterContract;
  initialMember: PayrollRosterMember;
  canRemove: boolean;
  // Suggestions only: say this person does not belong on payroll at all.
  onDismiss?: () => void;
  showInitialErrors: boolean;
  saving: boolean;
  dismissing: boolean;
  onSave: (member: PayrollRosterMember) => Promise<void>;
  onCancel: () => void;
  onRemove: () => void;
}

// The right-side edit surface: one tech at a time. Edits accumulate locally and
// only reach the draft on Save; Cancel throws them away. Nothing publishes here.
export const RosterEditDrawer = ({
  businessId,
  teams,
  accessFields,
  title,
  fields,
  fieldOptions,
  contract,
  initialMember,
  canRemove,
  onDismiss,
  showInitialErrors,
  saving,
  dismissing,
  onSave,
  onCancel,
  onRemove,
}: RosterEditDrawerProps) => {
  const initialValues = useMemo(
    () => toPayrollRosterFormValues(initialMember),
    [initialMember],
  );

  // Errors the person already had when the drawer opened. For a FLAGGED person
  // (a contract change invalidated a saved value, or made a blank field newly
  // required) these are non-empty; for a valid tech / new person they're empty.
  // Stable for the drawer's lifetime (it remounts per person via `key`).
  const initialErrors = useMemo(
    () => validatePayrollRosterMember(initialMember, fields, contract),
    [initialMember, fields, contract],
  );

  // Seed Formik's touched state with every field already in error at open, so a
  // pre-existing problem (a removed option, a newly-required blank) shows its
  // red border + inline message immediately — no interaction needed. Fields
  // that are valid at open stay un-nagged until the user touches them.
  const initialTouched = useMemo<FormikTouched<PayrollRosterMember>>(
    () =>
      showInitialErrors
        ? {
            display_name: Boolean(initialErrors.display_name),
            service_titan_employee_id: Boolean(
              initialErrors.service_titan_employee_id,
            ),
            field_values: Object.fromEntries(
              Object.keys(initialErrors.field_values).map((key) => [key, true]),
            ),
          }
        : {},
    [initialErrors, showInitialErrors],
  );

  return (
    <SimpleSidebar aria-label={title}>
      {/* Its own close button, not the header's: it's disabled while saving. */}
      <SimpleSidebarHeader
        className="px-6"
        title={title}
        description="Update all fields, then save the roster once."
        actions={
          <Button
            size="icon"
            variant="ghost"
            aria-label="Close editor"
            disabled={saving}
            onClick={onCancel}
            className="ml-auto shrink-0"
          >
            <XIcon />
          </Button>
        }
      />

      <Formik<PayrollRosterFormValues>
        initialValues={initialValues}
        initialTouched={initialTouched}
        validate={(member) =>
          toFormikErrors(validatePayrollRosterMember(member, fields, contract))
        }
        onSubmit={(values) => onSave(toPayrollRosterMember(values, fields))}
        validateOnMount
        enableReinitialize
      >
        {({
          errors,
          touched,
          getFieldProps,
          isSubmitting,
          submitCount,
          setFieldTouched,
          setFieldValue,
          values,
        }) => {
          const busy = saving || isSubmitting;
          const nameError = touched.display_name
            ? errors.display_name
            : undefined;
          const employeeIdError = touched.service_titan_employee_id
            ? errors.service_titan_employee_id
            : undefined;
          const attentionFields = fields.filter(
            (field) =>
              showInitialErrors &&
              field.key in initialErrors.field_values &&
              field.key in (errors.field_values ?? {}),
          );

          return (
            <Form className="flex min-h-0 flex-1 flex-col">
              <Stack
                align="start"
                gap={0}
                className="flex-1 overflow-y-auto p-6"
              >
                <Stack className="w-full" align="start" gap={6}>
                  {attentionFields.length > 0 && (
                    <Alert color="danger">
                      <Stack align="start" gap={1}>
                        <Text variant="label">Needs attention</Text>
                        <Text variant="caption" tone="muted">
                          {attentionFields
                            .map((field) => field.label)
                            .join(", ")}{" "}
                          {attentionFields.length === 1 ? "is" : "are"} no
                          longer valid. Pick{" "}
                          {attentionFields.length === 1
                            ? "a new value"
                            : "new values"}
                          .
                        </Text>
                      </Stack>
                    </Alert>
                  )}

                  {accessFields ?? (
                    <RosterMemberAccessFields
                      businessId={businessId}
                      teams={teams}
                      saving={busy}
                    />
                  )}
                  <DrawerField label="Name" required>
                    <Field data-invalid={Boolean(nameError) || undefined}>
                      <Input
                        aria-label="Name"
                        aria-invalid={nameError ? true : undefined}
                        disabled={busy}
                        {...getFieldProps("display_name")}
                      />
                      {nameError && <FieldError>{nameError}</FieldError>}
                    </Field>
                  </DrawerField>

                  <DrawerField label="ServiceTitan employee ID" required>
                    <Field data-invalid={Boolean(employeeIdError) || undefined}>
                      <Input
                        aria-label="ServiceTitan employee ID"
                        aria-invalid={employeeIdError ? true : undefined}
                        disabled={busy}
                        {...getFieldProps("service_titan_employee_id")}
                      />
                      {employeeIdError && (
                        <FieldError>{employeeIdError}</FieldError>
                      )}
                    </Field>
                  </DrawerField>

                  {fields.map((field) => {
                    const fieldError =
                      submitCount > 0 || touched.field_values?.[field.key]
                        ? errors.field_values?.[field.key]
                        : undefined;

                    return (
                      <DrawerField
                        key={field.key}
                        label={field.label}
                        required={field.required}
                      >
                        <RosterFieldControl
                          field={field}
                          options={fieldOptions[field.key] ?? []}
                          value={values.field_values[field.key]}
                          error={fieldError}
                          disabled={busy}
                          onChange={(value) => {
                            void setFieldValue(
                              `field_values.${field.key}`,
                              value,
                            );
                            void setFieldTouched(
                              `field_values.${field.key}`,
                              true,
                              false,
                            );
                          }}
                        />
                      </DrawerField>
                    );
                  })}
                </Stack>
              </Stack>

              <RosterEditDrawerFooter
                canRemove={canRemove}
                onDismiss={onDismiss}
                saving={busy}
                dismissing={dismissing}
                onCancel={onCancel}
                onRemove={onRemove}
              />
            </Form>
          );
        }}
      </Formik>
    </SimpleSidebar>
  );
};
